/**
 * Which Scheduler row a `?flow=` deep link means — the Banks Flow Tree's
 * "Go to Flow Optimiser" hands over a flow, and the drawer needs the row that
 * carries its campaign.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { OptimizerRow } from '@/features/optimizer-center/optimizer-center.types';
import { matchTracker, type TrackerId } from '@trafficpulse/shared';

/**
 * Tracker flow id first, then the flow name — the same two terms the link
 * builder can send (a local flow that has never been pushed has no id).
 *
 * Several campaigns can point at ONE Voluum flow, so a flow id can match more
 * than one row; the first wins and the (already filtered) list behind the
 * drawer still shows the rest. Returns undefined rather than guessing when
 * neither term matches — the flow simply has no optimizer config, and opening
 * some other campaign's drawer would be worse than opening none.
 *
 * Legacy links name only a flow and retain their Voluum/Binom matching.
 * New Tres Commas links also name their provider; imported ids can collide.
 */
export function findFlowRow(
  rows: OptimizerRow[],
  flow: string,
  provider?: TrackerId,
): OptimizerRow | undefined {
  const term = flow.trim().toLowerCase();
  if (!term) return undefined;
  const linkable = rows.filter((r) => provider ? r.provider === provider : matchTracker(r.provider, {
    voluum: () => true, binom: () => true, 'tres-commas': () => false,
  }));
  return (
    linkable.find((r) => r.flowId.toLowerCase() === term) ??
    linkable.find((r) => r.flowName.toLowerCase() === term)
  );
}
